import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertTriangle, CalendarDays, Loader2, Users } from 'lucide-react';
import { EDIT_REASONS } from './workbenchShared';
import { ProjectOptions, isOutsideProjectDates } from './ProjectOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const NONE = '__none__';

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  const dow = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - dow);
  return d;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const blankRow = (date, idx) => ({ date, dow: idx, include: false, start_time: '08:00', hours: '', project_id: NONE, error: null });
const presetRow = (date, idx, projectId) => ({ ...blankRow(date, idx), include: idx < 5, hours: idx < 5 ? '8' : '', project_id: idx < 5 ? projectId : NONE });

// Week Entry Mode (Oct 2026): one grid for a worker's whole week.
export const WeekEntryDialog = ({
  worker, onClose, token, startDate, onDone, crew = [],
  projects, projectsLoading, projectsError, onRetryProjects,
}) => {
  const defaultProject = worker?.default_project_id || NONE;
  const [copyTo, setCopyTo] = useState([]);
  const [crewOpen, setCrewOpen] = useState(false);
  const [crewSearch, setCrewSearch] = useState('');
  const [weekOf, setWeekOf] = useState(() => ymd(mondayOf(startDate || ymd(new Date()))));
  const [mode, setMode] = useState('preset');
  const [weekProject, setWeekProject] = useState(defaultProject);
  const [rows, setRows] = useState([]);
  const [reason, setReason] = useState('manual_adjustment');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState(null);

  const monday = useMemo(() => mondayOf(weekOf), [weekOf]);
  const dates = useMemo(() => Array.from({ length: 7 }, (_, i) => ymd(addDays(monday, i))), [monday]);

  const buildPreset = () => dates.map((d, i) => presetRow(d, i, weekProject));
  const buildBlank = () => dates.map((d, i) => blankRow(d, i));

  const copyPreviousWeek = async () => {
    setCopying(true);
    try {
      const prevStart = ymd(addDays(monday, -7));
      const prevEnd = ymd(addDays(monday, -1));
      const r = await axios.get(`${API}/reports/v2/payroll`, {
        params: { start_date: prevStart, end_date: prevEnd, worker_id: worker.worker_id },
        headers: { Authorization: `Bearer ${token}` },
      });
      const sessions = r.data?.rows?.[0]?.sessions || [];
      const byDow = {};
      for (const s of sessions) {
        const d = new Date(`${s.date}T12:00:00`);
        const dow = (d.getDay() + 6) % 7;
        const ci = new Date(s.clock_in);
        const b = byDow[dow] || { hours: 0, start: null, project_id: s.project_id };
        b.hours += Number(s.hours) || 0;
        const hhmm = `${pad(ci.getHours())}:${pad(ci.getMinutes())}`;
        if (!b.start || hhmm < b.start) b.start = hhmm;
        byDow[dow] = b;
      }
      setRows(dates.map((d, i) => {
        const b = byDow[i];
        if (!b) return blankRow(d, i);
        return { ...blankRow(d, i), include: true, start_time: b.start || '08:00', hours: String(Math.round(b.hours * 4) / 4), project_id: b.project_id || NONE };
      }));
      if (!sessions.length) setResult({ info: `No sessions found for ${worker.worker_name} in the week of ${prevStart}.` });
      else setResult(null);
    } catch {
      setResult({ info: 'Could not load the previous week — starting blank.' });
      setRows(buildBlank());
    } finally {
      setCopying(false);
    }
  };

  useEffect(() => {
    setResult(null);
    if (mode === 'preset') setRows(buildPreset());
    else if (mode === 'blank') setRows(buildBlank());
    else copyPreviousWeek();
  }, [mode, weekOf]);

  const applyWeekProject = (pid) => {
    setWeekProject(pid);
    setRows((rs) => rs.map((r) => ({ ...r, project_id: pid })));
  };
  const patch = (i, p) => setRows((rs) => rs.map((r, k) => (k === i ? { ...r, ...p, error: null } : r)));

  const included = rows.filter((r) => r.include && parseFloat(r.hours) > 0);
  const totalHours = included.reduce((a, r) => a + (parseFloat(r.hours) || 0), 0);
  const projMap = useMemo(() => Object.fromEntries((projects || []).map((p) => [p.id, p])), [projects]);
  const crewList = useMemo(() => (crew || []).filter((c) => c.worker_id !== worker?.worker_id && !c.locked), [crew, worker]);
  const filteredCrew = crewList.filter((c) => !crewSearch || (c.worker_name || '').toLowerCase().includes(crewSearch.toLowerCase()));

  const submit = async () => {
    const bad = rows.find((r) => r.include && (!(parseFloat(r.hours) > 0) || parseFloat(r.hours) > 24 || !r.start_time));
    if (bad) { patch(rows.indexOf(bad), { error: 'Hours must be 0.25–24 with a start time' }); return; }
    if (!included.length) return;
    const reasonLabel = EDIT_REASONS.find((r) => r.value === reason)?.label || reason;
    const sessions = included.map((r) => {
      const local = new Date(`${r.date}T${r.start_time}`);
      const p = projMap[r.project_id];
      return {
        clock_in: local.toISOString(),
        clock_out: new Date(local.getTime() + parseFloat(r.hours) * 3600 * 1000).toISOString(),
        ...(r.project_id !== NONE ? { project_id: r.project_id, project_name: p?.name || '', project_number: p?.project_number || '' } : {}),
        _date: r.date,
      };
    });
    setSaving(true);
    try {
      const body = (uid) => ({
        user_id: uid,
        reason: notes ? `[${reason}] ${reasonLabel} — week entry: ${notes}` : `[${reason}] ${reasonLabel} — week entry`,
        sessions: sessions.map(({ _date, ...s }) => s),
      });
      const res = await axios.post(`${API}/time-entries/admin-create-batch`, body(worker.worker_id), { headers: { Authorization: `Bearer ${token}` } });
      const errs = res.data.errors || [];
      if (errs.length) {
        setRows((rs) => rs.map((r) => {
          const e = errs.find((x) => sessions[x.index]?._date === r.date);
          return e ? { ...r, error: e.detail } : r;
        }));
      }
      // Copy-forward: apply the same week to every selected crew member.
      const crewResults = [];
      for (const uid of copyTo) {
        const cw = (crew || []).find((c) => c.worker_id === uid);
        try {
          const cr = await axios.post(`${API}/time-entries/admin-create-batch`, body(uid), { headers: { Authorization: `Bearer ${token}` } });
          crewResults.push({ worker_id: uid, name: cw?.worker_name || uid, created: cr.data.created_count, errors: cr.data.error_count, hours: cr.data.total_hours });
        } catch (e) {
          crewResults.push({ worker_id: uid, name: cw?.worker_name || uid, created: 0, errors: sessions.length, hours: 0, detail: e.response?.data?.detail });
        }
      }
      const totalCreated = res.data.created_count + crewResults.reduce((a, c) => a + c.created, 0);
      const totalErrors = errs.length + crewResults.reduce((a, c) => a + c.errors, 0);
      const totalHours = Math.round((res.data.total_hours + crewResults.reduce((a, c) => a + (c.hours || 0), 0)) * 100) / 100;
      setResult({ created: totalCreated, errors: totalErrors, hours: totalHours, crew: crewResults });
      onDone?.({ created_count: totalCreated, error_count: totalErrors, total_hours: totalHours, crew: crewResults });
      if (!totalErrors) onClose();
    } catch (e) {
      setResult({ info: e.response?.data?.detail || 'Could not add the week.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!worker} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="pw-week-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Add a week of sessions</DialogTitle>
          <DialogDescription>
            Fill in the days <b>{worker?.worker_name}</b> worked. Each checked day becomes one admin-created session.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Week of (Monday)</Label>
            <Input type="date" value={weekOf} onChange={(e) => e.target.value && setWeekOf(ymd(mondayOf(e.target.value)))} data-testid="pw-week-of" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Start from</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="pw-week-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="preset" data-testid="pw-week-mode-preset">Mon–Fri · 8h from 8:00 AM</SelectItem>
                <SelectItem value="copy" data-testid="pw-week-mode-copy">Copy this worker's previous week</SelectItem>
                <SelectItem value="blank" data-testid="pw-week-mode-blank">All seven days blank</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Project for the whole week</Label>
            <Select value={weekProject} onValueChange={applyWeekProject}>
              <SelectTrigger data-testid="pw-week-project"><SelectValue placeholder="Pick per day" /></SelectTrigger>
              <SelectContent className="max-h-[320px]">
                <SelectItem value={NONE} data-testid="pw-week-project-none">Pick per day / no project</SelectItem>
                <ProjectOptions projects={projects} projectsLoading={projectsLoading} projectsError={projectsError}
                  onRetry={onRetryProjects} recentIds={[...new Set((worker?.sessions || []).map((s) => s.project_id).filter(Boolean))]}
                  testPrefix="pw-week-project" />
              </SelectContent>
            </Select>
            {worker?.default_project_id && (
              <p className="text-[11px] text-slate-500">Prefilled from this worker's default project (Staff → Edit).</p>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-[#1F2D3F] overflow-x-auto">
          <table className="w-full text-sm" data-testid="pw-week-grid">
            <thead className="bg-[#0F1923] text-[11px] uppercase tracking-wide text-slate-400">
              <tr>
                <th className="px-2 py-2 w-8" />
                <th className="px-2 py-2 text-left">Day</th>
                <th className="px-2 py-2 text-left w-32">Start</th>
                <th className="px-2 py-2 text-left w-24">Hours</th>
                <th className="px-2 py-2 text-left">Project</th>
              </tr>
            </thead>
            <tbody>
              {copying && (
                <tr><td colSpan={5} className="px-3 py-4 text-xs text-slate-400"><Loader2 className="inline w-3 h-3 mr-1 animate-spin" /> Loading previous week…</td></tr>
              )}
              {!copying && rows.map((r, i) => {
                const outside = isOutsideProjectDates(projMap[r.project_id], r.date);
                return (
                  <tr key={r.date} className={`border-t border-[#1F2D3F] ${r.include ? '' : 'opacity-60'}`} data-testid={`pw-week-row-${r.date}`}>
                    <td className="px-2 py-1.5">
                      <Checkbox checked={r.include} onCheckedChange={(v) => patch(i, { include: !!v, hours: v && !r.hours ? '8' : r.hours })} data-testid={`pw-week-include-${r.date}`} />
                    </td>
                    <td className="px-2 py-1.5 text-white whitespace-nowrap">
                      <span className="font-medium">{DAY_NAMES[r.dow]}</span> <span className="text-slate-400 text-xs">{r.date.slice(5)}</span>
                      {r.error && <div className="text-[11px] text-rose-400 flex items-center gap-1" data-testid={`pw-week-error-${r.date}`}><AlertTriangle className="w-3 h-3" />{r.error}</div>}
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="time" className="h-8" value={r.start_time} disabled={!r.include} onChange={(e) => patch(i, { start_time: e.target.value })} data-testid={`pw-week-start-${r.date}`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" className="h-8" min="0.25" max="24" step="0.25" value={r.hours} disabled={!r.include} onChange={(e) => patch(i, { hours: e.target.value })} data-testid={`pw-week-hours-${r.date}`} />
                    </td>
                    <td className="px-2 py-1.5">
                      <Select value={r.project_id} onValueChange={(v) => patch(i, { project_id: v })} disabled={!r.include}>
                        <SelectTrigger className="h-8" data-testid={`pw-week-project-${r.date}`}><SelectValue placeholder="No project" /></SelectTrigger>
                        <SelectContent className="max-h-[320px]">
                          <SelectItem value={NONE}>No project</SelectItem>
                          <ProjectOptions projects={projects} projectsLoading={projectsLoading} projectsError={projectsError}
                            onRetry={onRetryProjects} testPrefix={`pw-week-proj-${r.date}`} forDate={r.date} />
                        </SelectContent>
                      </Select>
                      {outside && <p className="text-[10px] text-amber-400 mt-0.5" data-testid={`pw-week-outside-${r.date}`}>Outside project dates — will be flagged</p>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Copy-forward: apply this week to the rest of the crew */}
        {crewList.length > 0 && (
          <div className="rounded-lg border border-[#1F2D3F]" data-testid="pw-week-crew">
            <button type="button" onClick={() => setCrewOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2 text-left" data-testid="pw-week-crew-toggle">
              <span className="text-sm text-white flex items-center gap-2"><Users className="w-4 h-4 text-sky-400" /> Also apply this week to other crew members
                {copyTo.length > 0 && <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300" data-testid="pw-week-crew-count">{copyTo.length} selected</span>}
              </span>
              <span className="text-xs text-slate-400">{crewOpen ? 'Hide' : 'Show'}</span>
            </button>
            {crewOpen && (
              <div className="px-3 pb-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input placeholder="Search workers…" className="h-8" value={crewSearch} onChange={(e) => setCrewSearch(e.target.value)} data-testid="pw-week-crew-search" />
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setCopyTo(filteredCrew.map((c) => c.worker_id))} data-testid="pw-week-crew-select-all">Select all shown</Button>
                  <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setCopyTo([])} data-testid="pw-week-crew-clear">Clear</Button>
                </div>
                <div className="max-h-40 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {filteredCrew.map((c) => (
                    <label key={c.worker_id} className="flex items-center gap-2 text-sm text-slate-200 px-1 py-0.5 rounded hover:bg-[#0F1923] cursor-pointer">
                      <Checkbox checked={copyTo.includes(c.worker_id)} onCheckedChange={(v) => setCopyTo((ids) => (v ? [...ids, c.worker_id] : ids.filter((x) => x !== c.worker_id)))} data-testid={`pw-week-crew-${c.worker_id}`} />
                      <span className="truncate">{c.worker_name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500">Same days, start times, hours and projects are created for each selected worker. Locked or already-logged days fail individually and are listed afterwards.</p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Reason (audit) *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="pw-week-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{EDIT_REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. No cell coverage on site all week" data-testid="pw-week-notes" />
          </div>
        </div>

        {result?.info && <p className="text-xs text-amber-400" data-testid="pw-week-info">{result.info}</p>}
        {result?.created !== undefined && (
          <div className="text-xs text-slate-300 space-y-1" data-testid="pw-week-result">
            <p>Added {result.created} session{result.created === 1 ? '' : 's'} ({result.hours}h){result.errors ? ` · ${result.errors} day${result.errors === 1 ? '' : 's'} failed — see rows above` : ''}.</p>
            {(result.crew || []).map((c) => (
              <p key={c.worker_id} className={c.errors ? 'text-amber-400' : 'text-slate-400'} data-testid={`pw-week-crew-result-${c.worker_id}`}>
                {c.name}: {c.created} added{c.errors ? `, ${c.errors} failed${c.detail ? ` (${c.detail})` : ''}` : ''}
              </p>
            ))}
          </div>
        )}

        <DialogFooter className="items-center gap-2">
          <span className="text-xs text-slate-400 mr-auto" data-testid="pw-week-total">
            {included.length} day{included.length === 1 ? '' : 's'} · {Math.round(totalHours * 100) / 100}h
            {copyTo.length > 0 && <> · × {copyTo.length + 1} workers</>}
          </span>
          <Button variant="outline" onClick={onClose} data-testid="pw-week-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || copying || !included.length} className="bg-[#1E88E5] hover:bg-blue-700" data-testid="pw-week-save">
            {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Adding…</> : <>Add {included.length * (copyTo.length + 1)} session{included.length * (copyTo.length + 1) === 1 ? '' : 's'}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
